import { useRef, useState, useEffect } from 'react';
import { getEnabledPlatforms } from '../utils/accountSettingPlatformList';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faUserCog,
  faUserSecret,
  faTimesCircle,
  faFolder,
  faChevronLeft,
  faChevronRight,
  faBarChart,
  faChartLine,
  faBullseye,
  faDesktop,
  faTableCells,
  faBolt,
  faSignOutAlt,
  faChevronDown,
  faCheck,
  faKey,
  faTags
} from '@fortawesome/free-solid-svg-icons';
import './NewSidebar.css';
import { isAdmin, isSuperAdmin, canSendMessage, canSeeTriggers } from '../utils/permissions';
import { brandLogos, brandName } from '../utils/branding';
import { useAccount } from '../contexts/AccountContext';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { faClock, faLightbulb, faImages, faClipboardCheck, faFileImport } from '@fortawesome/free-solid-svg-icons';

function NewSidebar({
  onLogout,
  user,
  onChatSelect,
  onNewChat,
  onEndImpersonation,
  isCollapsed,
  onToggleCollapse
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const dropdownRef = useRef(null);
  const portalDropdownRef = useRef(null);

  // State for custom dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);

  // Use the account context
  const {
    accounts,
    selectedAccountId,
    selectAccount,
    getAccountIdToUse
  } = useAccount();

  // Get account settings to check for pacing-enabled, media-planner-enabled, and oauth-enabled
  const accountId = getAccountIdToUse();
  const { settings: accountSettings, loadedAccountId } = useAccountSettings(accountId, user);

  // Check pacing-enabled (comma-separated list of platforms, e.g. "facebook,ttd")
  const pacingEnabledPlatforms = getEnabledPlatforms(accountSettings?.['pacing-enabled']);
  const isPacingEnabled = pacingEnabledPlatforms.length > 0;


  // Check media-planner-enabled (comma-separated list of platforms, e.g. "facebook,google-ads")
  const mediaPlannerEnabledPlatforms = getEnabledPlatforms(accountSettings?.['media-planner-enabled']);
  const isMediaPlannerEnabled = mediaPlannerEnabledPlatforms.length > 0;

  // Check oauth-enabled (comma-separated list of platforms, e.g. "facebook")
  const oauthEnabledPlatforms = getEnabledPlatforms(accountSettings?.['oauth-enabled']);
  const isOAuthEnabled = oauthEnabledPlatforms.length > 0;

  // Check creative-manager-enabled (comma-separated list of platforms, e.g. "facebook,reddit,ttd,google-ads")
  const creativeManagerEnabledPlatforms = getEnabledPlatforms(accountSettings?.['creative-manager-enabled']);
  const isCreativeManagerEnabled = creativeManagerEnabledPlatforms.length > 0;

  // Check campaign-qa-enabled (comma-separated list of platforms, e.g. "dv360")
  const campaignQAEnabledPlatforms = getEnabledPlatforms(accountSettings?.['campaign-qa-enabled']);
  const isCampaignQAEnabled = campaignQAEnabledPlatforms.length > 0;

  // Check frequence-enabled for Madhive file-based integration
  const isFrequenceEnabled = accountSettings?.['frequence-enabled'] === 'true';

  // Campaign Grid visibility: every member of a flag-on account, whatever their
  // role, plus super admins always. The sidebar is the grid's only entry point
  // since the Campaigns toolbar button went (#3165), and the grid API already
  // serves any account member — an admin-only check here left Campaign
  // Managers on flag-on accounts with no way in.
  const campaignGridUiEnabled = isSuperAdmin(user)
    || accountSettings?.['enable-v3-ui-campaign-grid'] === 'true';
  // Triggers: super admins always; account admins only when its flag is on.
  const perfGridUiEnabled = canSeeTriggers(user, accountSettings);
  // Performance section (Diagnostics / Budget & pacing / Recommendations /
  // Moments). Flag-only, no role gate — deliberately matching what the backend
  // enforces in utils/performanceTabFlag.js. A role gate here that the server
  // does not apply would hide the nav entry from someone the API would happily
  // serve, which reads as a bug rather than a policy.
  const performanceUiEnabled = accountSettings?.['enable-performance-tab'] === 'true';
  const optimizationWorkspaceEnabled = accountSettings?.['performance-v2.0'] === 'true';

  // Handle account selection change
  const handleAccountSelect = (accountId) => {
    selectAccount(accountId);
    setIsDropdownOpen(false);
  };

  // Handle dropdown toggle
  const toggleDropdown = () => {
    if (!isDropdownOpen && dropdownRef.current) {
      const rect = dropdownRef.current.querySelector('.account-select-button').getBoundingClientRect();
      setButtonRect(rect);
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown button
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Also check if click is outside the portal dropdown
        if (!portalDropdownRef.current || !portalDropdownRef.current.contains(event.target)) {
          setIsDropdownOpen(false);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  // Navigation items configuration
  const navigationItems = [
    ...(loadedAccountId === accountId && accountSettings?.['offers-enabled'] === 'true' ? [
      { id: 'offers', icon: faTags, label: 'Offers', onClick: () => navigate('/offers'), isActive: location.pathname.startsWith('/offers') }
    ] : []),
    {
      id: 'new-campaign',
      icon: faPlus,
      label: 'New Campaign',
      onClick: () => navigate('/campaign/new'),
      disabled: !canSendMessage(user) || (isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId),
      tooltip: isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId
        ? "Please select an account first"
        : "Create a new campaign",
      isPrimary: true
    },
    {
      id: 'folders',
      icon: faFolder,
      label: 'Folders',
      onClick: () => {
        navigate('/folders');
      },
      isActive: location.pathname.includes('/folders')
    },
    {
      id: 'campaigns',
      icon: faDesktop,
      label: 'Campaigns',
      onClick: () => {
        navigate('/campaigns');
      },
      isActive: location.pathname === '/campaigns' || location.pathname.startsWith('/campaigns/')
    },
    // Conditionally add Campaign Grid (performance grid) if enabled
    ...(campaignGridUiEnabled ? [{
      id: 'campaign-grid',
      icon: faTableCells,
      label: 'Campaign Grid',
      onClick: () => navigate('/campaign-grid'),
      isActive: location.pathname.startsWith('/campaign-grid')
    }] : []),
    // Conditionally add Triggers (perf-agent recommendations) if enabled
    ...(perfGridUiEnabled ? [{
      id: 'perf-grid',
      icon: faBolt,
      label: 'Triggers',
      onClick: () => navigate('/perf-grid'),
      isActive: location.pathname.startsWith('/perf-grid')
    }] : []),
    // Conditionally add the Performance section if enabled for this account
    ...(performanceUiEnabled ? [{
      id: 'performance',
      icon: faChartLine,
      label: 'Performance',
      onClick: () => navigate('/performance'),
      isActive: location.pathname.startsWith('/performance')
    }] : []),
    ...(optimizationWorkspaceEnabled ? [{
      id: 'optimization-workspace',
      icon: faBullseye,
      label: 'Optimization',
      onClick: () => navigate('/optimization-workspace'),
      isActive: location.pathname === '/optimization-workspace'
    }] : []),
    // Conditionally add Pacing item if enabled for this account
    ...(isPacingEnabled ? [{
      id: 'pacing',
      icon: faClock,
      label: 'Pacing',
      onClick: () => navigate('/pacing'),
      isActive: location.pathname.startsWith('/pacing')
    }] : []),
    // Conditionally add Media Plans item if enabled for this account
    ...(isMediaPlannerEnabled ? [{
      id: 'media-plans',
      icon: faLightbulb,
      label: 'Media Plans',
      onClick: () => navigate('/media-plans'),
      isActive: location.pathname.startsWith('/media-plans')
    }] : []),
    // Conditionally add Creative Manager item if enabled for this account
    ...(isCreativeManagerEnabled ? [{
      id: 'creative-manager',
      icon: faImages,
      label: 'Creative Manager',
      onClick: () => navigate('/creative-manager'),
      isActive: location.pathname.startsWith('/creative-manager')
    }] : []),
    // Conditionally add Campaign QA item if enabled for this account
    ...(isCampaignQAEnabled ? [{
      id: 'campaign-qa',
      icon: faClipboardCheck,
      label: 'Campaign QA',
      onClick: () => navigate('/campaign-qa'),
      isActive: location.pathname.startsWith('/campaign-qa')
    }] : []),
    // Conditionally add Frequence integration tracking page
    ...(isFrequenceEnabled ? [{
      id: 'frequence',
      icon: faFileImport,
      label: 'Frequence',
      onClick: () => navigate('/frequence'),
      isActive: location.pathname.startsWith('/frequence')
    }] : []),
    // {
    //   id: 'reports',
    //   icon: faBarChart,
    //   label: 'Reports',
    //   onClick: () => {
    //     onChatSelect(null);
    //     navigate('/reports');
    //   },
    //   disabled: isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId,
    //   tooltip: isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId
    //     ? 'Please select an account to view reports'
    //     : 'View reports',
    //   isActive: location.pathname.includes('/reports')
    // },
    {
      id: 'status',
      icon: faChartLine,
      label: 'Status',
      onClick: () => {
        onChatSelect(null);
        navigate('/status');
      },
      disabled: isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId,
      tooltip: isAdmin(user) && !isSuperAdmin(user) && accounts.length > 0 && !selectedAccountId
        ? 'Please select an account to view status'
        : 'View status',
      isActive: location.pathname.includes('/status')
    },
    // OAuth - only show when account has oauth-enabled setting
    ...(accountId && isOAuthEnabled ? [{
      id: 'oauth',
      icon: faKey,
      label: 'OAuth',
      onClick: () => {
        onChatSelect(null);
        navigate('/oauth');
      },
      tooltip: 'Manage OAuth connections',
      isActive: location.pathname.includes('/oauth')
    }] : [])
  ];

  // Add admin item if user is admin
  if (isAdmin(user)) {
    navigationItems.push({
      id: 'admin',
      icon: faUserCog,
      label: 'Admin',
      onClick: () => {
        onChatSelect(null);
        navigate('/admin');
      }
    });
  }

  return (
    <div
      ref={sidebarRef}
      className={`new-sidebar ${isCollapsed ? 'collapsed' : 'expanded'}`}
    >
      <div className="new-sidebar-content">
        {/* Header with logo */}
        <div className="new-sidebar-header">
          <div className="new-sidebar-logo-title">
            <div className="new-sidebar-logo">
              <img
                src={isCollapsed ? brandLogos.square : brandLogos.white}
                alt={brandName}
                className="logo-image"
              />
            </div>

            {!isCollapsed && (
              <div className="sidebar-title">
                {/* Logo-white.png already contains the text, so no additional text needed */}
              </div>
            )}
          </div>

          {!isCollapsed && (
            <button
              className="collapse-button"
              onClick={onToggleCollapse}
              title="Collapse sidebar"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <span>Collapse</span>
            </button>
          )}
        </div>

        {/* Expand button for collapsed state */}
        {isCollapsed && (
          <div className="expand-button-container">
            <button
              className="expand-button"
              onClick={onToggleCollapse}
              title="Expand sidebar"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}

        {/* Impersonation banner (only when expanded) */}
        {!isCollapsed && user.isImpersonating && (
          <div className="impersonation-banner">
            <div className="impersonation-info">
              <FontAwesomeIcon icon={faUserSecret} />
              <div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Impersonating</div>
                <div className="impersonation-user">{user.display_name || user.email}</div>
              </div>
            </div>
            <button
              onClick={onEndImpersonation}
              className="end-impersonation-btn"
            >
              <FontAwesomeIcon icon={faTimesCircle} />
              <span>End Impersonation</span>
            </button>
          </div>
        )}

        {/* Account selector for regular admins (only when expanded). Hidden
            when the user has exactly one account — AccountContext auto-selects
            it so this would just be a one-option dropdown. */}
        {!isCollapsed && isAdmin(user) && !isSuperAdmin(user) && accounts.length > 1 && (
          <div className="new-sidebar-account-selector">
            <div className="account-selector-label">
              {selectedAccountId
                ? "Showing chats for selected account"
                : "Select an account"}
            </div>
            <div className="account-selector-dropdown" ref={dropdownRef}>
              <button
                className="account-select-button"
                onClick={toggleDropdown}
              >
                <span className="account-select-text">
                  {selectedAccountId
                    ? accounts.find(acc => acc.id === selectedAccountId)?.name || "Unknown Account"
                    : "-- Please select an account --"
                  }
                </span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`account-select-arrow ${isDropdownOpen ? 'open' : ''}`}
                />
              </button>
            </div>
          </div>
        )}

        {/* Navigation items */}
        <div className="new-sidebar-nav">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              className={`nav-item ${item.disabled ? 'disabled' : ''} ${item.isActive ? 'active' : ''} ${item.isPrimary ? 'primary' : ''}`}
              onClick={item.onClick}
              disabled={item.disabled}
              title={isCollapsed ? item.label : (item.tooltip || item.label)}
            >
              <div className="nav-item-icon">
                <FontAwesomeIcon icon={item.icon} />
              </div>
              {!isCollapsed && <span className="nav-item-label">{item.label}</span>}
            </button>
          ))}
        </div>

        {/* User profile at bottom */}
        <div className="new-sidebar-user">
          <div className="user-avatar">
            {user.picture_url ? (
              <img src={user.picture_url} alt={user.given_name} />
            ) : (
              <span>
                {(user.given_name || user.display_name || user.email || 'U').charAt(0)}
              </span>
            )}
          </div>
          {!isCollapsed && (
            <div className="user-info">
              <span className="user-name">Account</span>
              <button
                className="logout-btn"
                onClick={onLogout}
                title="Logout"
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Portal dropdown */}
      {isDropdownOpen && buttonRect && createPortal(
        <div
          ref={portalDropdownRef}
          className="account-dropdown-list"
          style={{
            position: 'fixed',
            top: buttonRect.bottom + 4,
            left: buttonRect.left,
            width: buttonRect.width,
            zIndex: 99999
          }}
        >
          {!selectedAccountId && (
            <div className="account-dropdown-item placeholder">
              -- Please select an account --
            </div>
          )}
          {accounts.map(account => (
            <button
              key={account.id}
              className={`account-dropdown-item ${selectedAccountId === account.id ? 'selected' : ''}`}
              onClick={() => handleAccountSelect(account.id)}
            >
              <span className="account-name">{account.name}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default NewSidebar;
